import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast, useToast } from "@shared/hooks/use-toast";
import {
  useRemoteUtilityId,
  useTenantId,
} from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ConfigMapKey } from "../configMap.constants";
import {
  getCategory,
  getSubCategory,
  onboardingApi,
  searchPlan,
  getUnitWiseGraph,
  GetAvailableResourcesParams,
  getAvailableResources,
  UpdateResourceUnitPayload,
  updateResourceUnit,
  GetResourceUnitAssignmentParams,
  getResourceUnitAssignments,
  ReplaceResourcePayload,
  replaceResource,
} from "./api";
import { formatMonthYear } from "./components/calender-schedule/utils";
import { tarrifFilters } from "./components/plan-tariffs/components/Tarrif/types";
import {
  AddHolidayPayload,
  EntityFilters,
  EntityOption,
  FormOption,
  IdTypeWithDetails,
  PlanDashboardData,
  planFilters,
  RateDetail,
  ServiceAreaFilters,
  ToggleUtilityPayload,
  userFilters,
  Utility,
} from "./types";

// Hook to get all utilities
export const useUtilities = () => {
  const tenantId = useTenantId();
  const key = QueryKeyFactory.module.onboarding.utilitySetup();
  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getUtilities(tenantId),
    {
      enabled: !!tenantId,
    },
  );
};

// Hook to get all utilities
export const useProgressData = () => {
  const tenantId = useTenantId();
  const key = QueryKeyFactory.module.onboarding.utilityProgress();
  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getUtilityProgressData(tenantId),
    {
      enabled: !!tenantId,
    },
  );
};

export const useSystemAccessModules = () => {
  const tenantId = useTenantId();
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId && !!tenantId;
  const key = QueryKeyFactory.module.onboarding.coreSettings.systemAdminList();
  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getSubscriptionData(remoteUtilityId, tenantId),
    {
      enabled, // ✅ Only run when both IDs are ready
    },
  );
};

export const useCreateUtilityMutation = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (data: any): Promise<Utility> => onboardingApi.createUtility(data),
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Utility created successfully",
        });
        // ✅ Invalidate utilitySetup query key
        //

        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.utilitySetup(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.utilityProgress(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.orgSetup(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create utility",
          variant: "destructive",
        });
      },
    },
  );
};

// Hook to update utility
export const useUpdateUtility = () => {
  const { toast } = useToast();

  return useSmartMutation(
    ({
      formData,
      id,
    }: {
      formData: FormData;
      id: number;
    }): Promise<Utility> => {
      return onboardingApi.updateUtility(formData, id);
    },

    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Utility updated successfully",
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.utilitySetup(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.utilityProgress(),
        });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.orgSetup(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update utility",
          variant: "destructive",
        });
      },
    },
  );
};

export const useUpdateUnit = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => {
      return onboardingApi.utilityUnitChange(payload);
    },
    {
      onSuccess: (data) => {
        toast({
          title: "Success!",
          description: data.message,
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.onboarding.coreSettings.allUtilityService(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update unit",
          variant: "destructive",
        });
      },
    },
  );
};

export const useUtilityStatus = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => {
      return onboardingApi.updateUtilityStatus(payload);
    },
    {
      onSuccess: (data) => {
        toast({
          title: "Success!",
          description: data.message,
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.onboarding.coreSettings.allUtilityService(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update unit",
          variant: "destructive",
        });
      },
    },
  );
};

// Hook to toggle utility status
export const useToggleUtilityStatus = () => {
  const { toast } = useToast();
  const key = QueryKeyFactory.global.utility.list();

  return useSmartMutation(
    ({ id, isActive }: ToggleUtilityPayload) =>
      onboardingApi.toggleUtilityStatus(id, isActive),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.utilityProgress(),
        });

        toast({
          title: "Success",
          description: "Utility status updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message || "Failed to update utility status",
          variant: "destructive",
        });
      },
      invalidateKeys: [key],
    },
  );
};

// Hook to toggle contracts status
export const useToggleContractsStatus = () => {
  const { toast } = useToast();
  const key = QueryKeyFactory.global.utility.list();

  return useSmartMutation(
    ({ id, isActive }: any) => onboardingApi.toggleContractStatus(id, isActive),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.onboarding.coreSettings.ContractList(),
        });

        toast({
          title: "Success",
          description: "Contract status updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error.message || "Failed to update Contract status",
          variant: "destructive",
        });
      },
      invalidateKeys: [key],
    },
  );
};

// Hook to delete utility
export const useDeleteUtility = () => {
  return useSmartMutation(onboardingApi.deleteUtility, {
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Utility deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to delete utility",
        variant: "destructive",
      });
    },
    invalidateKeys: [
      QueryKeyFactory.global.utility.list(),
      QueryKeyFactory.module.onboarding.progress(),
    ],
  });
};

// Hook for setup progress
export const useSetupProgress = () => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.progress(),
    onboardingApi.getSetupProgress,
  );
};

// Hook to get all utilities
export const useDashboardSummary = () => {
  const remote_utility_id = useRemoteUtilityId();

  const enabled = !!remote_utility_id;
  const key = QueryKeyFactory.module.onboarding.utilityDashboard();
  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getUtilityDashboardSummary(remote_utility_id),
    { enabled },
  );
};

export const useUtilitySetup = () => {
  const remote_utility_id = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.utilitySetupProgres();
  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getUtilitySetup(remote_utility_id),
    { enabled: !!remote_utility_id },
  );
};

export const useCategories = () => {
  const remote_utility_id = useRemoteUtilityId();

  const enabled = !!remote_utility_id;
  const key = QueryKeyFactory.module.onboarding.categories();
  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getCategoriesData(remote_utility_id),
    { enabled },
  );
};

// Hook to get revenuw details
export const useUtilityRevenue = () => {
  const remote_utility_id = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.utilityRevenue();
  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getUtilityRevenue(remote_utility_id),
    {
      enabled: !!remote_utility_id,
    },
  );
};

// Hook to get customer serviece
export const useCustomerServiceStats = () => {
  const remote_utility_id = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.customerService();
  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getCustomerService(remote_utility_id),
    {
      enabled: !!remote_utility_id,
    },
  );
};

// Hook to get work order serviece
export const useWorkOrderServiceStats = () => {
  const remote_utility_id = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.workOrderService();
  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getWorkOrderService(remote_utility_id),
    {
      enabled: !!remote_utility_id,
    },
  );
};

// Hook to get work order serviece
export const useUserMgmtDashboard = () => {
  const remote_utility_id = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.userMgmtDashboard();
  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getUtilityMgmtDashboard(remote_utility_id),
    {
      enabled: !!remote_utility_id,
    },
  );
};

export const useUserTable = (
  filters: userFilters = {},
  remoteUtilityId: string,
) => {
  const remote_utility_id = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.userMgmtTable(filters);

  const enabled = !!remote_utility_id;

  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getUtilityMgmtList(remote_utility_id, filters),
    { enabled }, // ✅ Only run when ID is ready
  );
};

// export const useDepartmentOptions = () => {
//   const utilityId = useRemoteUtilityId();
//   const enabled = !!utilityId;

//   const key = QueryKeyFactory.module.onboarding.departmentOptions();
//   return useSmartQuery(
//     key as unknown[],
//     () => onboardingApi.getDepartments(utilityId),
//     { enabled }
//   );
// };

// export const useRoleOptions = (department?: string) => {
//   const utilityId = useRemoteUtilityId();

//   // Only enable the query if we have both utilityId AND department
//   const enabled = !!utilityId && !!department;

//   const key = QueryKeyFactory.module.onboarding.roleOptions(department || "");

//   return useSmartQuery(
//     key as unknown[],
//     () => onboardingApi.getRoles(utilityId, department),
//     {
//       enabled,
//       // Don't fetch if department is not selected
//       staleTime: 300_000, // Cache for 5 minutes
//     }
//   );
// };

export const useDepartmentOptions = () => {
  const utilityId = useRemoteUtilityId();
  const enabled = !!utilityId;
  const key = QueryKeyFactory.module.onboarding.departmentOptions();
  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getDepartments(utilityId),
    { enabled },
  );
};

export const useRoleOptions = (departments?: string[]) => {
  const utilityId = useRemoteUtilityId();
  // Only enable the query if we have both utilityId AND at least one department
  const enabled = !!utilityId && !!departments && departments.length > 0;

  // Create a stable key that includes all departments sorted (for consistent caching)
  const key = QueryKeyFactory.module.onboarding.roleOptions(
    departments?.sort().join(",") || "",
  );

  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getRoles(utilityId, departments),
    {
      enabled,
      staleTime: 300_000, // Cache for 5 minutes
    },
  );
};

export const useInviteUser = () => {
  const { toast } = useToast();

  return useSmartMutation(onboardingApi.inviteUser, {
    onSuccess: () => {
      toast({ title: "Users invited successfully!" });
      queryClient.invalidateQueries({
        queryKey: ["onboarding", "consumer", "list"],
        exact: false,
      });
      queryClient.refetchQueries({
        queryKey: ["mx", "meter-reader"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: QueryKeyFactory.module.onboarding.userMgmtDashboard(),
      });
    },
    onError: (error: any) => {
      // toast({ title: "Error", description: error.message });
    },
  });
};
export const useUpdateRoleConfiguration = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => onboardingApi.updateRoleConfiguration(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Role configuration updated successfully",
        });
        // Invalidate role permissions query to refresh the data
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.onboarding.coreSettings.RolePermissionList(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update role configuration",
          variant: "destructive",
        });
      },
    },
  );
};
export const useUpdateUser = () => {
  const { toast } = useToast();

  return useSmartMutation(
    ({ id, payload }: { id: number; payload: any }) =>
      onboardingApi.updateInviteUser(id, payload),
    {
      onSuccess: (data) => {
        toast({ title: data.message });

        // Invalidate all userMgmtTable queries (regardless of filters)
        queryClient.invalidateQueries({
          queryKey: ["onboarding", "consumer", "list"],
          exact: false,
        });

        // Also invalidate the specific userMgmtTable query
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.userMgmtTable(),
        });
      },
      onError: (error: any) => {
        toast({ title: "Error", description: error.message });
      },
    },
  );
};
export const useResendInvite = () => {
  const { toast } = useToast();

  return useSmartMutation(
    ({ email }: { email: string }) => onboardingApi.resendUser({ email }),
    {
      onSuccess: () => {
        toast({ title: "Invitation send!" });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.userMgmtTable(),
        });
        queryClient.invalidateQueries({
          queryKey: ["onboarding", "consumer", "list"],
          exact: false,
        });
      },
      onError: (error: any) => {
        toast({ title: "Error", description: error.message });
      },
    },
  );
};

export const useResendInviteOrg = () => {
  const { toast } = useToast();

  return useSmartMutation(
    ({ email }: { email: string }) => onboardingApi.resendUserOrg({ email }),
    {
      onSuccess: () => {
        toast({ title: "User updated successfully!" });
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.users(
            1,
            Number(PAGE_SIZE),
          ),
        });
      },
      onError: (error: any) => {
        toast({ title: "Error", description: error.message });
      },
    },
  );
};

export const useLeaveTypes = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.leaveType();
  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getLeaveTypes(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};
export const useAddHolidayMutation = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: AddHolidayPayload) => onboardingApi.addHoliday(payload),
    {
      onSuccess: (_data, payload) => {
        toast({ title: "Holiday added successfully!" });

        // Use same format as used for fetching holidayList
        const monthYear = payload.date
          ? formatMonthYear(new Date(payload.date))
          : undefined;

        if (monthYear) {
          queryClient.invalidateQueries({
            queryKey: QueryKeyFactory.module.onboarding.holidayList(monthYear),
          });
        }
      },
      onError: (error: any) => {
        toast({ title: "Error", description: error.message });
      },
    },
  );
};
export const useUpdateHolidayMutation = () => {
  const { toast } = useToast();

  return useSmartMutation(
    ({ payload, id }: { payload: AddHolidayPayload; id: number }) =>
      onboardingApi.updateHoliday(payload, id),
    {
      onSuccess: (_data, { payload }) => {
        toast({ title: "Holiday Updated successfully!" });

        const monthYear = payload.date
          ? formatMonthYear(new Date(payload.date))
          : undefined;

        if (monthYear) {
          queryClient.invalidateQueries({
            queryKey: QueryKeyFactory.module.onboarding.holidayList(monthYear),
          });
        }
      },
      onError: (error: any) => {
        toast({ title: "Error", description: error.message });
      },
    },
  );
};
export const useDeleteHolidayMutation = () => {
  const { toast } = useToast();

  return useSmartMutation((id: number) => onboardingApi.deleteHoliday(id), {
    onSuccess: (_data, id) => {
      toast({ title: "Holiday deleted successfully!" });

      // Optional: if you have a selected date, invalidate that month's list
      const monthYear = formatMonthYear(new Date());
      queryClient.invalidateQueries({
        queryKey: QueryKeyFactory.module.onboarding.holidayList(monthYear),
      });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message });
    },
  });
};

export const useAddWorkingHour = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => onboardingApi.addWorkingHour(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.workingHourList(),
        });
      },
      onError: (error: any) => {
        toast({ title: "Error", description: error.message });
      },
    },
  );
};

export const useHolidayList = (date: Date) => {
  const remoteUtilityId = useRemoteUtilityId();
  const formattedMonthYear = formatMonthYear(date); // e.g., "July 2025"

  const key = QueryKeyFactory.module.onboarding.holidayList(formattedMonthYear);

  return useSmartQuery(
    key,
    () => onboardingApi.getHolidayItems(remoteUtilityId, formattedMonthYear),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useWorkingist = () => {
  const remoteUtilityId = useRemoteUtilityId();

  const key = QueryKeyFactory.module.onboarding.workingHourList();

  return useSmartQuery(
    key,
    () => onboardingApi.getWorkingItems(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const useRateTypeOptions = () => {
  const key = QueryKeyFactory.module.onboarding.planTarrif.rateTypes();
  return useSmartQuery(key as unknown[], () => onboardingApi.getRateTypes());
};

export const useBasisTypeOptions = (rateType: string) => {
  const key = QueryKeyFactory.module.onboarding.planTarrif.basisTypes(rateType);
  return useSmartQuery(key as unknown[], () =>
    onboardingApi.getBasisTypes(Number(rateType)),
  );
};

export const useBillFrequencyOptions = () => {
  const key = QueryKeyFactory.module.onboarding.planTarrif.billFrequency();
  return useSmartQuery(key as unknown[], () =>
    onboardingApi.getBillFrequency(),
  );
};

export const useTaxOptions = () => {
  const key = QueryKeyFactory.module.onboarding.planTarrif.taxType();
  return useSmartQuery(key as unknown[], () => onboardingApi.getTaxType());
};

export const useTerritoryPremiseStats = (territoryCode: string) => {
  const remoteUtilityId = useRemoteUtilityId();

  const key =
    QueryKeyFactory.module.onboarding.serviceArea.territoryPremiseStats(
      remoteUtilityId,
      territoryCode,
    );

  // ✅ Run query only if both remoteUtilityId and territoryCode are present
  const enabled = !!remoteUtilityId && !!territoryCode;

  return useSmartQuery(
    key as unknown[],
    () =>
      onboardingApi.getTerritoryPremiseStats(remoteUtilityId, territoryCode),
    { enabled },
  );
};

export const useMonthlySummary = (remoteUtilityId: string) => {
  const key =
    QueryKeyFactory.module.onboarding.serviceArea.monthlySummary(
      remoteUtilityId,
    );
  return useSmartQuery(key, () =>
    onboardingApi.getMonthlySummary(remoteUtilityId),
  );
};

export const useCreateRateMutation = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (data: any): Promise<Utility> => onboardingApi.addRate(data),
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Rate created successfully",
        });

        queryClient.refetchQueries({
          queryKey: ["onboarding", "rateList"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create rate",
          variant: "destructive",
        });
      },
    },
  );
};

export const useUpdateRateMutation = (id: number) => {
  const { toast } = useToast();

  return useSmartMutation(
    (data: any): Promise<Utility> => onboardingApi.updateRate(data, id),
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Rate Updated successfully",
        });
        queryClient.refetchQueries({
          queryKey: ["onboarding", "rateList"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update rate",
          variant: "destructive",
        });
      },
    },
  );
};

export const useToggleRateMutation = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (data: { id: number; is_active: boolean }): Promise<Utility> =>
      onboardingApi.updateRate(data, data.id),
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Rate status updated successfully",
        });
        queryClient.refetchQueries({
          queryKey: ["onboarding", "rateList"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to toggle rate status",
          variant: "destructive",
        });
      },
    },
  );
};

export const useTogglePlanMutation = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (data: { id: number; is_active: boolean }): Promise<Utility> =>
      onboardingApi.updatePlan(data, data.id),
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Plan status updated successfully",
        });
        queryClient.refetchQueries({
          queryKey: ["onboarding", "planList"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to toggle Plan status",
          variant: "destructive",
        });
      },
    },
  );
};

export const useTarrifList = (
  filters: tarrifFilters = {},
  remoteUtilityId: string,
) => {
  const remote_utility_id = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.planTarrif.rateTable(filters);

  const enabled = !!remote_utility_id;

  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getTarrifList(remote_utility_id, filters),
    { enabled }, // ✅ Only run when ID is ready
  );
};

export const usePlanVersion = ({
  planId,
  page,
  pageSize,
}: {
  planId: number;
  page: number;
  pageSize: number;
}) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planTarrif.planVersion(
      planId,
      page,
      pageSize,
    ),
    () =>
      onboardingApi.getPlanVersionList({
        remoteUtilityId,
        planId,
        page,
        pageSize,
      }),
    {
      enabled: !!planId && !!remoteUtilityId,
      refetchOnWindowFocus: false,
    },
  );
};

export const usePlanList = (filters: planFilters = {}) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planTarrif.planTable(filters),
    () => onboardingApi.getPlanList(remoteUtilityId, filters),
    {
      enabled: !!remoteUtilityId,
    },
  );
};
export const useCreatePlanMutation = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (data: any): Promise<any> => onboardingApi.addPlan(data),
    {
      onSuccess: (data) => {
        queryClient.refetchQueries({
          queryKey: ["onboarding", "planList"],
        });
        toast({
          title: "Success!",
          description: "Plan created successfully",
        });
        // Return the data to ensure it flows to component callbacks
        return data;
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create plan",
          variant: "destructive",
        });
        // Re-throw to ensure component onError gets called
        throw error;
      },
    },
  );
};

export const useUpdatePlanMutation = (id: number) => {
  const { toast } = useToast();

  return useSmartMutation(
    (data: any): Promise<Utility> => onboardingApi.updatePlan(data, id),
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Plan Updated successfully",
        });
        queryClient.refetchQueries({
          queryKey: ["onboarding", "planList"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create Plan",
          variant: "destructive",
        });
      },
    },
  );
};

export const useEntityTypes = () => {
  const remote_utility_id = useRemoteUtilityId();
  const queryKey = QueryKeyFactory.module.onboarding.serviceArea.EntityTypes();
  return useSmartQuery(queryKey as unknown[], () =>
    onboardingApi.getEntityChoices(),
  );
};

export const useTerritoryJson = (configLevel: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  const queryKey = QueryKeyFactory.module.onboarding.serviceArea.territoryJson(
    remoteUtilityId,
    configLevel,
  );
  const enabled = !!remoteUtilityId && !!configLevel;

  return useSmartQuery(
    queryKey as unknown[],
    () => onboardingApi.getTerritoryJson(remoteUtilityId, configLevel),
    { enabled },
  );
};

export const useConfigCode = (entityName: string) => {
  const queryKey =
    QueryKeyFactory.module.onboarding.serviceArea.configCode(entityName);
  const enabled = !!entityName && entityName.trim().length > 0;

  return useSmartQuery(
    queryKey as unknown[],
    () => onboardingApi.getConfigCode(entityName),
    {
      enabled,
      staleTime: 0,
      refetchOnMount: true,
    },
  );
};

export const useExportTerritoryMapping = () => {
  return useMutation({
    mutationFn: (params: {
      remoteUtilityId: string;
      page: number;
      limit: number;
      territoryCode?: string;
      searchData?: string;
      isPaginationRequired?: string;
      filters?: EntityFilters;
    }) =>
      onboardingApi.exportTerritoryMapping(
        params.remoteUtilityId,
        params.page,
        params.limit,
        params.territoryCode,
        params.searchData,
        params.isPaginationRequired,
        params.filters,
      ),
  });
};

// export const useTerritoryMapping = (
//   page: number = 1,
//   limit: number = 10,
//   territoryCode?: string,
//   searchData?: string,
//   isPaginationRequired?: string,
//   p0?: { enabled: boolean }
// ) => {
//   const remoteUtilityId = useRemoteUtilityId();
//   console.log("territoryCode at hook", territoryCode);
//   const queryKey =
//     QueryKeyFactory.module.onboarding.serviceArea.territoryMapping({
//       page,
//       limit,
//       territoryCode,
//       searchData,
//       isPaginationRequired,
//     });

//   const enabled = !!remoteUtilityId;

//   return useSmartQuery(
//     queryKey as unknown[],
//     () =>
//       onboardingApi.getTerritoryMapping(
//         remoteUtilityId,
//         page,
//         limit,
//         territoryCode,
//         searchData,
//         isPaginationRequired
//       ),
//     { enabled }
//   );
// };

export const useTerritoryMapping = (
  page: number = 1,
  limit: number = 10,
  territoryCode?: string,
  searchData?: string,
  isPaginationRequired?: string,
  filters?: EntityFilters,
) => {
  const remoteUtilityId = useRemoteUtilityId();

  // Updated query key to match the factory signature
  const queryKey =
    QueryKeyFactory.module.onboarding.serviceArea.territoryMapping({
      page,
      limit,
      territoryCode,
      searchData,
      isPaginationRequired,
      territoryType: filters?.territoryType,
      status: filters?.status,
      createdOn: filters?.createdOn,
      search_data: filters?.search_data,
    });

  const enabled = !!remoteUtilityId && !!territoryCode;

  return useSmartQuery(
    queryKey as unknown[],
    () =>
      onboardingApi.getTerritoryMapping(
        remoteUtilityId,
        page,
        limit,
        territoryCode,
        searchData,
        isPaginationRequired,
        filters,
      ),
    { enabled },
  );
};

export const useTerritoryTypeOptions = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    ["territory-types", remoteUtilityId],
    () => onboardingApi.getTerritoryTypes(remoteUtilityId),
    { enabled: !!remoteUtilityId },
  );
};

export const useRecentlyUpdatedRates = (limit: number = 3) => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planTarrif.recentlyUpdatedRates(limit),
    () => onboardingApi.getRecentlyUpdatedRates(remoteUtilityId, limit),
    { enabled },
  );
};

export const useRecentlyUpdatedPlans = (limit = 3) => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planTarrif.recentlyUpdatedPlans(limit),
    () => onboardingApi.getRecentlyUpdatedPlans(remoteUtilityId, limit),
    { enabled },
  );
};

export const useAllUtilityServices = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.coreSettings.allUtilityService(),
    () => onboardingApi.getAllUtilityService(remoteUtilityId),
    {
      enabled,
    },
  );
};
export const useTerritoryCount = () => {
  const remoteUtilityId = useRemoteUtilityId();

  const key = QueryKeyFactory.module.onboarding.serviceArea.Count();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getTerritoryCount(remoteUtilityId),
    {
      enabled,

      refetchOnWindowFocus: false,
    },
  );
};
export const useCategoryOptions = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.planTarrif.category();
  return useSmartQuery(key, () => onboardingApi.getCategories(remoteUtilityId));
};

export const useSubcategoryOptions = (categoryCode: string) => {
  const remoteUtilityId = useRemoteUtilityId();

  const key =
    QueryKeyFactory.module.onboarding.planTarrif.subCategory(categoryCode); // ✅ dynamic key
  return useSmartQuery(
    key,
    () => onboardingApi.getSubcategories(remoteUtilityId, categoryCode),
    {
      enabled: !!remoteUtilityId && !!categoryCode,
    },
  );
};

export const useRateNameOptions = (
  queryParams: { type: string; value: string; utilityService?: string } | null,
) => {
  const remoteUtilityId = useRemoteUtilityId();

  // Create a unique key that includes utility service
  const key = queryParams
    ? QueryKeyFactory.module.onboarding.planTarrif.rateNames(
        `${queryParams.type}_${queryParams.value}_${
          queryParams.utilityService || "all"
        }`,
      )
    : [];

  return useSmartQuery(key, () => {
    // Pass the utility service parameter to the API
    return onboardingApi.getTypeWiseRate(
      remoteUtilityId,
      queryParams.value,
      queryParams.type,
      queryParams.utilityService, // Pass the utility service
    );
  });
};

export const useServiceChargeName = () => {
  const remoteUtilityId = useRemoteUtilityId();

  const key = QueryKeyFactory.module.onboarding.planTarrif.serviceCharges(); // ✅ dynamic key
  return useSmartQuery(
    key,
    () => onboardingApi.getServiceCharges(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    },
  );
};

export const usePlanDashboard = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery<PlanDashboardData>(
    QueryKeyFactory.module.onboarding.planTarrif.planKPIs(),
    () => onboardingApi.getPlanDashboardData(remoteUtilityId),
    { enabled },
  );
};

export const useRateDetail = (id: number) => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId && !!id;

  return useSmartQuery<RateDetail>(
    QueryKeyFactory.module.onboarding.planTarrif.rateDetail(id),
    () => onboardingApi.getRateDetail(id, remoteUtilityId),
    { enabled },
  );
};

// export const useDeleteHoliday = () => {
//   return useSmartMutation(onboardingApi.deleteUtility, {
//     onSuccess: () => {
//       toast({
//         title: "Success!",
//         description: "Utility deleted successfully",
//       });
//     },
//     onError: (error: any) => {
//       toast({
//         title: "Error",
//         description: error?.message || "Failed to delete utility",
//         variant: "destructive",
//       });
//     },
//     invalidateKeys: [
//       QueryKeyFactory.global.utility.list(),
//       QueryKeyFactory.module.onboarding.progress(),
//     ],
//   });
// };

export const useEntityTypesMaster = () => {
  const key = QueryKeyFactory.module.onboarding.entityTypesMaster();

  return useSmartQuery(key, () => onboardingApi.getEntityTypes(true));
};

export const useEntityTypesTransaction = () => {
  const key = QueryKeyFactory.module.onboarding.entityTypesTransaction();

  return useSmartQuery(key, () => onboardingApi.getEntityTypes(false));
};

export const useUtilityServices = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.utilityServices();

  return useSmartQuery(key, () =>
    onboardingApi.getUtilityServices(remoteUtilityId),
  );
};

export const useDateFormatChoices = () => {
  const key = QueryKeyFactory.module.onboarding.dateFormatChoices();

  return useSmartQuery(key, () => onboardingApi.getDateFormatChoices());
};

export const useSeparatorChoices = () => {
  const key = QueryKeyFactory.module.onboarding.separatorChoices();

  return useSmartQuery(key, () => onboardingApi.getSeparatorChoices());
};

export const useEntityOptions = (
  selectedIdType: "master" | "transaction" | "",
): EntityOption[] => {
  const { data: masterData } = useEntityTypesMaster(); // returns EntityTypeApiResponse
  const { data: transactionData } = useEntityTypesTransaction();

  if (selectedIdType === "master" && masterData?.result) {
    return masterData.result.map((entity) => ({
      value: entity.id.toString(),
      label: entity.name,
      meta: {
        moduleId: entity.moduleId,
        moduleName: entity.moduleName,
      },
    }));
  }

  if (selectedIdType === "transaction" && transactionData?.result) {
    return transactionData.result.map((entity) => ({
      value: entity.id.toString(),
      label: entity.name,
      meta: {
        moduleId: entity.moduleId,
        moduleName: entity.moduleName,
      },
    }));
  }

  return [];
};

export const useUtilityOptions = (): FormOption[] => {
  const { data } = useUtilityServices();

  if (!data?.result) return [];

  // Filter only active services and format for form options
  return data.result
    .filter((service) => service.isActive && service.id !== null)
    .map((service) => ({
      value: service.code,
      label: `${service.name} (${service.code})`,
    }));
};

export const useDateFormatOptions = (): FormOption[] => {
  const { data } = useDateFormatChoices();

  if (!data?.result) return [];

  return data.result.map((choice) => ({
    value: choice.key,
    label: choice.value,
  }));
};

export const useSeparatorOptions = (): FormOption[] => {
  const { data } = useSeparatorChoices();

  if (!data?.result) return [];

  return data.result.map((choice) => ({
    value: choice.key,
    label: choice.value,
  }));
};
export const useIdTypeOptions = (): IdTypeWithDetails[] => {
  const key = QueryKeyFactory.module.onboarding.idTypes();

  const { data } = useSmartQuery(key, () => onboardingApi.getIdTypes());

  if (!data?.result) return [];

  return data.result.map((choice) => ({
    type: choice.key === 0 ? "master" : "transaction",
    title: choice.value,
    description: choice.description,
  }));
};

export const useCreateIdConfigMutation = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => onboardingApi.createIdConfig(payload),
    {
      onSuccess: () => {
        toast({
          title: "ID Configuration created successfully!",
        });
        const key = QueryKeyFactory.module.onboarding.numberFormats();
        // ✅ Invalidate numberFormats query
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.numberFormats(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to create ID Configuration",
          description: error.message,
        });
      },
    },
  );
};
export const useNumberFormats = (tab: string, page: number = 1, limit: number = 10) => {
  const remoteUtilityId = useRemoteUtilityId();

  // Create conditional query key based on tab value
  const key = QueryKeyFactory.module.onboarding.numberFormats(
    tab === "master"
      ? "master"
      : tab === "transaction"
        ? "transaction"
        : undefined,
    page,
    limit
  );

  // Only enable query when we have both remoteUtilityId and a valid tab
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    key,
    () => onboardingApi.getNumberFormats(remoteUtilityId, tab, page, limit),
    {
      enabled, // Only run when conditions are met
      staleTime: 300_000, // Optional: cache for 5 minutes like other hooks
    },
  );
};

export const useUpdatSystemAdminAccess = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => {
      return onboardingApi.updateSystemAdminAccess(payload);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Access updated successfully",
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.onboarding.coreSettings.systemAdminList(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update Access",
          variant: "destructive",
        });
      },
    },
  );
};

export const useRolesPermission = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.coreSettings.RolePermissionList(),
    () => onboardingApi.getRolesPermissionnData(remoteUtilityId),
    {
      enabled,
    },
  );
};

export const useCreatedUserNames = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planTarrif.createdUserNames(),
    () => onboardingApi.getCreatedUsers(remoteUtilityId),
    {
      enabled,
    },
  );
};

export const useContract = (categories?: any, subCategories?: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.coreSettings.FilteredContractList(
      categories,
      subCategories,
    ),
    () =>
      onboardingApi.getContractsData(
        remoteUtilityId,
        categories,
        subCategories,
      ),
    {
      enabled,
    },
  );
};

export const useTerritoryEntityView = (territoryCode?: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId && !!territoryCode;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.serviceArea.TerritoryView(),
    () => onboardingApi.getEntityData(remoteUtilityId, territoryCode),
    {
      enabled,
    },
  );
};

export const useServiceAreaStats = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.serviceArea.TerritoryStats(),
    () => onboardingApi.getServiceAreaStats(remoteUtilityId),
    {
      enabled,
    },
  );
};

export const useUserNames = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.serviceArea.TerritoryStats(),
    () => onboardingApi.getUserNames(remoteUtilityId),
    {
      enabled,
    },
  );
};

export const useAuditLogs = (filters: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  const enabled = !!remoteUtilityId;

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.auditLogs(filters),
    () => onboardingApi.getauditLogs(remoteUtilityId),
    {
      enabled,
    },
  );
};

export const useUpdateContract = () => {
  const { toast } = useToast();

  return useSmartMutation(
    ({ id, formData }: { id: number; formData: FormData }) => {
      return onboardingApi.useUpdateContract(id, formData);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Contract updated successfully",
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.onboarding.coreSettings.ContractList(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update contract",
          variant: "destructive",
        });
      },
    },
  );
};

export const useDeleteContract = () => {
  const { toast } = useToast();

  return useSmartMutation(
    ({ id }: { id: number }) => {
      return onboardingApi.useDeleteContract(id);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Contract deleted successfully",
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.onboarding.coreSettings.ContractList(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to deleted contract",
          variant: "destructive",
        });
      },
    },
  );
};

export const useCreateContract = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => onboardingApi.createContract(payload),
    {
      onSuccess: () => {
        toast({
          title: "Contract created successfully!",
        });

        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.onboarding.coreSettings.ContractList(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Contract create ID Configuration",
          description: error.message,
        });
      },
    },
  );
};

export const useEntityChoices = () => {
  const key = QueryKeyFactory.module.onboarding.separatorChoices();

  return useSmartQuery(key, () => onboardingApi.getSeparatorChoices());
};

export const getEntityTypesWithoutPagination = (level: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  const key =
    QueryKeyFactory.module.onboarding.serviceArea.EntityTypeNonPaginated(level);

  return useSmartQuery(
    key,
    () => onboardingApi.getEntityTypesWithoutPagination(remoteUtilityId, level),
    {
      enabled: !!level,
    },
  );
};

export const useConfigMapData = <T = any>(
  remoteUtilityId: string,
  mapType: ConfigMapKey,
  configLevel: string,
  codeList?: string, // Add optional codeList parameter as string
  showInactive?: boolean,
  isPaginationRequired: boolean = false,
  filters: ServiceAreaFilters = {},
) => {
  return useQuery({
    queryKey: [
      "configMap",
      mapType,
      remoteUtilityId,
      configLevel,
      codeList,
      filters,
    ],
    queryFn: () =>
      onboardingApi.getItems<T>({
        remoteUtilityId,
        mapType,
        configLevel,
        codeList,
        showInactive,
        isPaginationRequired,
        filters,
      }),
    enabled: !!remoteUtilityId && !!configLevel,
    staleTime: 300_000, // 5 minutes
  });
};
export const usePostConfigMap = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (params: any) => {
      return onboardingApi.postItem(params);
    },
    {
      onSuccess: (data) => {
        toast({
          title: data.message,
        });
        queryClient.invalidateQueries({
          queryKey: ["configMap"],
        });

        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.serviceArea.Count(),
        });

        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.onboarding.serviceArea.TerritoryStats(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create item",
          variant: "destructive",
        });
      },
    },
  );
};

export const usePatchConfigMap = () => {
  const { toast } = useToast();
  return useSmartMutation(
    (params: any) => {
      return onboardingApi.patchItem(params);
    },
    {
      onSuccess: (_data, params) => {
        toast({
          title: "Success!",
          description: "Item updated successfully",
        });

        // Always invalidate configMap queries
        queryClient.invalidateQueries({
          queryKey: ["configMap"],
        });
        queryClient.refetchQueries({
          queryKey: ["onboarding", "territory-premise-stats"],
        });

        // Invalidate territoryPremiseStats query so detail APIs automatically refresh
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey.some(
              (key) =>
                typeof key === "string" &&
                (key.includes("territoryPremiseStats") ||
                  key.includes("territory-premise-stats")),
            ),
        });
        queryClient.refetchQueries({
          queryKey: ["onboarding", "consumerOnboarding", "category"],
        });

        // Invalidate subcategory query used by contract (mx/route/subCategory)
        queryClient.invalidateQueries({
          queryKey: ["mx", "route", "subCategory"],
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update item",
          variant: "destructive",
        });
      },
    },
  );
};
export const useUpdateIdConfigMutation = () => {
  const { toast } = useToast();

  return useSmartMutation(
    ({ id, payload }: { id: string | number; payload: any }) =>
      onboardingApi.updateNumberFormat(id, payload),
    {
      onSuccess: () => {
        toast({
          title: "ID Configuration updated successfully!",
        });
        // Invalidate numberFormats query to refresh the table
        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.onboarding.numberFormats(),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to update ID Configuration",
          description: error.message,
          variant: "destructive",
        });
      },
    },
  );
};

export const useUpdateIdEntity = () => {
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartMutation(
    ({ id, payload }: { id: number; payload: any }) =>
      onboardingApi.updateEntityData(id, payload, remoteUtilityId),
    {
      onSuccess: () => {
        toast({
          title: "Entity Assigned successfully!",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to  Assigne Entity ",
          description: error.message,
          variant: "destructive",
        });
      },
    },
  );
};

export const usePlanDetail = (planId: any) => {
  const remote_utility_id = useRemoteUtilityId();
  const key = QueryKeyFactory.module.onboarding.planTarrif.planDetail(planId);

  const enabled = !!remote_utility_id && !!planId;

  return useSmartQuery(
    key as unknown[],
    () => onboardingApi.getPlanDetail(remote_utility_id, planId),
    { enabled },
  );
};

export const useGetCategory = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.cx.consumerOnboarding.category(),
    () => getCategory(utilityId),
  );
};

export const useGetSubCategory = (code_list: any[]) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.mx.routesManagement.subCategory(code_list),
    () => getSubCategory(utilityId, code_list),
    {
      enabled: !!code_list?.length && !!utilityId,
    },
  );
};

export const useGetParameters = (
  entityType?: string,
  status?: string,
  search?: string,
  page: number = 1,
  limit: number = 10,
) => {
  const utilityId = useRemoteUtilityId();
  const enabled = !!utilityId;
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.parameter(
      utilityId,
      entityType,
      status,
      search,
      page,
      limit,
    ),
    () =>
      onboardingApi.getParameter(
        utilityId,
        entityType,
        status,
        search,
        page,
        limit,
      ),
    {
      enabled,
    },
  );
};

export const useCreateParameter = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => onboardingApi.createParameter(payload),
    {
      onSuccess: () => {
        toast({
          title: "Parameter created successfully!",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to create parameter",
          description: error.message,
          variant: "destructive",
        });
      },
    },
  );
};

export const useUpdateParameter = () => {
  const { toast } = useToast();

  return useSmartMutation(
    ({ id, payload }: { id: string; payload: any }) =>
      onboardingApi.updateParameter(id, payload),
    {
      onSuccess: () => {
        toast({
          title: "Parameter updated successfully!",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed to update parameter",
          description: error.message,
          variant: "destructive",
        });
      },
    },
  );
};

export const useParameterSummary = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planRates.planRatesSummary(utilityId),
    () => onboardingApi.getParameterSummary(utilityId),
    {
      enabled: !!utilityId,
    },
  );
};

export const useEntityTypeForParameters = () => {
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planRates.entityTypeForParameters(),
    () => onboardingApi.getEntityTypesForParameters(),
  );
};

export const useSourceModules = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planRates.sourceModules(utilityId),
    () => onboardingApi.getSourceModules(utilityId),
    {
      enabled: !!utilityId,
    },
  );
};

export const useValuesForSource = (id: string) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.onboarding.planRates.ValuesForSource(utilityId, id),
    () => onboardingApi.getValuesForSource(utilityId, id),
    {
      enabled: !!utilityId && !!id,
    },
  );
};

export const usePlanSearch = (params: {
  remoteUtilityId: string;
  utilityService?: string[] | string;
  searchQuery?: string;
  page?: number;
  limit?: number;
}) => {
  const fallbackId = String(getRemoteUtilityId() || "");
  const effectiveUtilityId = params.remoteUtilityId || fallbackId;
  const serviceKey = Array.isArray(params.utilityService)
    ? params.utilityService.join(",")
    : params.utilityService || "";

  const searchParams = {
    ...params,
    remoteUtilityId: effectiveUtilityId,
    searchQuery: params.searchQuery || "",
    page: params.page || 1,
    limit: params.limit || 10,
  };

  return useSmartQuery(
    QueryKeyFactory.module.onboarding.searchPlan({
      remoteUtilityId: effectiveUtilityId,
      serviceKey,
      searchQuery: params.searchQuery || "",
      page: params.page || 1,
      limit: params.limit || 10,
    }),
    () => searchPlan(searchParams),
    {
      enabled: !!effectiveUtilityId,
      staleTime: 0,
      refetchOnMount: true,
    },
  );
};

export const useUnitWiseGraph = (
  unit: string,
  utilityService: string = "Water",
  fetchLastSixRecords: boolean = true,
  enabled: boolean = true,
) => {
  const remoteUtilityId = useRemoteUtilityId();

  const queryKey = [
    "unitWiseGraph",
    remoteUtilityId,
    utilityService,
    unit,
    fetchLastSixRecords,
  ];

  return useSmartQuery(
    queryKey,
    () =>
      getUnitWiseGraph({
        remoteUtilityId: remoteUtilityId || "",
        utilityService,
        unit,
        fetchLastSixRecords,
      }),
    {
      enabled: enabled && !!remoteUtilityId && !!unit && !!utilityService,
    },
  );
};

export const useNscAddConsumers = ({
  remoteUtilityId,
  searchData,
  page,
  limit,
  enabled = true,
}: {
  remoteUtilityId?: string | number;
  searchData?: string;
  page?: number;
  limit?: number;
  enabled?: boolean;
}) => {
  const defaultRemoteUtilityId = useRemoteUtilityId();
  const utilityId = remoteUtilityId || defaultRemoteUtilityId;

  const queryKey = [
    "onboarding",
    "serviceArea",
    "nscAddConsumers",
    utilityId,
    searchData,
    page,
    limit,
  ];

  return useSmartQuery(
    queryKey as unknown[],
    () => onboardingApi.getNscAddConsumers(utilityId, searchData, page, limit),
    {
      enabled: enabled && !!utilityId,
    },
  );
};

export const useUnitWiseConsumers = ({
  unitCode,
  remoteUtilityId,
  isCurrent,
  searchData,
  page,
  limit,
  enabled = true,
}: {
  unitCode?: string;
  remoteUtilityId?: string | number;
  isCurrent?: number;
  searchData?: string;
  page?: number;
  limit?: number;
  enabled?: boolean;
}) => {
  const defaultRemoteUtilityId = useRemoteUtilityId();
  const utilityId = remoteUtilityId || defaultRemoteUtilityId;

  const queryKey = [
    "onboarding",
    "serviceArea",
    "unitWiseConsumers",
    unitCode,
    utilityId,
    isCurrent,
    searchData,
    page,
    limit,
  ];

  return useSmartQuery(
    queryKey as unknown[],
    () =>
      onboardingApi.getUnitWiseConsumers(
        unitCode,
        utilityId,
        isCurrent,
        searchData,
        page,
        limit,
      ),
    {
      enabled: enabled && !!unitCode && !!utilityId,
    },
  );
};

export const useUpdateAssignedServiceAddress = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: any) =>
      onboardingApi.updateAssignedServiceAddress(params),
    onSuccess: () => {
      toast({
        title: "Consumer attached",
        description: "Service address and consumer attached successfully.",
      });
      queryClient.refetchQueries({
        queryKey: ["onboarding", "serviceArea", "unitWiseConsumers"],
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Attachment failed",
        description:
          error?.response?.data?.message ||
          "Failed to update assigned service address.",
      });
    },
  });
};

export const useGetAvailableResources = (
  params: GetAvailableResourcesParams = {},
  options?: { enabled?: boolean },
) => {
  const remoteUtilityIdFromStore = useRemoteUtilityId();
  const remoteUtilityId = params.remoteUtilityId ?? remoteUtilityIdFromStore;

  const queryParams = {
    ...params,
    remoteUtilityId,
  };

  const key = [
    "mx",
    "resource-management",
    "available",
    remoteUtilityId,
    params.utilityService,
    params.resourceTypeCode,
    params.resourceSubtypeCode,
  ];

  return useSmartQuery(
    key,
    () => onboardingApi.getAvailableResources(queryParams),
    {
      enabled:
        options?.enabled !== undefined ? options.enabled : !!remoteUtilityId,
    },
  );
};

export const useAvailableResources = useGetAvailableResources;

export const useUpdateResourceUnit = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useSmartMutation(
    ({
      resourceId,
      payload,
    }: {
      resourceId: string | number;
      payload: UpdateResourceUnitPayload;
    }) => onboardingApi.updateResourceUnit(resourceId, payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Resource unit updated successfully.",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "resource-management", "available"],
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "resource-unit-assignment"],
        });
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Update failed",
          description:
            error?.message ||
            error?.response?.data?.message ||
            "Failed to update resource unit.",
        });
      },
    },
  );
};

export const useGetResourceUnitAssignments = (
  params: GetResourceUnitAssignmentParams = {},
  options?: { enabled?: boolean },
) => {
  const remoteUtilityIdFromStore = useRemoteUtilityId();
  const remoteUtilityId = params.remoteUtilityId ?? remoteUtilityIdFromStore;

  const queryParams = {
    ...params,
    remoteUtilityId,
  };

  const key = [
    "mx",
    "resource-unit-assignment",
    remoteUtilityId,
    params.unitCode,
    params.searchData,
    params.utilityService,
    params.isExport,
    params.isHistory,
  ];

  return useSmartQuery(
    key,
    () => onboardingApi.getResourceUnitAssignments(queryParams),
    {
      enabled:
        options?.enabled !== undefined ? options.enabled : !!remoteUtilityId,
    },
  );
};

export const useReplaceResource = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useSmartMutation(
    (payload: ReplaceResourcePayload) =>
      onboardingApi.replaceResource(payload),
    {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Resource replaced successfully.",
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "resource-unit-assignment"],
        });
        queryClient.invalidateQueries({
          queryKey: ["mx", "resource-management", "available"],
        });
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Replacement failed",
          description:
            error?.message ||
            error?.response?.data?.message ||
            "Failed to replace resource.",
        });
      },
    },
  );
};

